import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiHelpCircle,
  FiPhone,
  FiMail,
  FiMapPin,
  FiSend,
  FiChevronDown,
  FiCheckCircle,
  FiMessageSquare,
  FiClock,
  FiLifeBuoy,
  FiSearch,
  FiGift,
  FiFolder,
  FiTool,
} from "react-icons/fi";
import { showToast } from "../../common/notification/Notification";
import "../../common/formkit/formkit.css";
import "./CustomerHelpSupport.css";

export const FAQS = [
  {
    id: 1,
    question: "How do I contact a property owner or listing agent?",
    answer:
      "Navigate to any property or listing detail page. You will find dedicated 'Call' and 'Chat' options to directly connect with the verified owner or agent.",
  },
  {
    id: 2,
    question: "How do I update my profile and contact preferences?",
    answer:
      "Go to your Account settings in the top right menu, then click 'Edit Profile'. You can update your phone number, email, address, and city location.",
  },
  {
    id: 3,
    question: "How do Reward Points and Loyalty Cash work?",
    answer:
      "You earn points by interacting with properties, inviting contacts, verifying your details, and sharing promotions. Once you reach the minimum balance, points can be redeemed towards subscription benefits.",
  },
  {
    id: 4,
    question: "How can I join or create a Neighborhood Community?",
    answer:
      "Visit the 'Communities' section from the left navigation. You can explore public neighborhood communities and join them instantly, or click 'Create Community' to build your own local circle.",
  },
  {
    id: 5,
    question: "How do I book or inquire about Home Services?",
    answer:
      "Explore the 'Services Directory' tab to discover verified architects, contractors, interior designers, and service providers. Click any service to view photos, prices, and schedule a meeting reminder.",
  },
];

const QUICK_LINKS = [
  { label: "Rewards", description: "Points and redeem", to: "/customer/rewards", icon: FiGift },
  { label: "My Properties", description: "Listings you created", to: "/customer/my-properties", icon: FiFolder },
  { label: "Home Services", description: "Find trusted providers", to: "/customer/services", icon: FiTool },
];

const MAX_CHARS = 500;
const PHONE = "+919818961783";
const EMAIL = "support@allhomedesire.com";

export default function CustomerHelpSupport() {
  const [expandedFaq, setExpandedFaq] = useState(1);
  const [faqSearch, setFaqSearch] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const handleToggleFaq = (id) => {
    setExpandedFaq((prev) => (prev === id ? null : id));
  };

  const visibleFaqs = useMemo(() => {
    const q = faqSearch.trim().toLowerCase();
    return q ? FAQS.filter((f) => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q)) : FAQS;
  }, [faqSearch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!message.trim()) {
      setErrorMsg("Please enter your support message.");
      return;
    }
    if (message.trim().length < 10) {
      setErrorMsg("Please provide more details (minimum 10 characters).");
      return;
    }

    setSubmitting(true);
    // Simulates inquiry dispatch matching mobile Help.js
    setTimeout(() => {
      setSubmitting(false);
      const done = "Thank you! Your inquiry has been sent to our customer support team. We will get back to you shortly.";
      setSuccessMsg(done);
      showToast("Inquiry sent to customer support", { type: "success" });
      setMessage("");
      setSubject("");
    }, 1200);
  };

  const counterClass =
    message.length >= MAX_CHARS ? "hs-counter hs-counter--max" : message.length >= MAX_CHARS * 0.9 ? "hs-counter hs-counter--warn" : "hs-counter";

  return (
    <div className="hs-page">
      {/* Hero */}
      <section className="hs-hero" aria-labelledby="hs-title">
        <div className="hs-hero__text">
          <span className="hs-badge">
            <FiLifeBuoy size={14} aria-hidden="true" /> Support
          </span>
          <h1 className="hs-hero__title" id="hs-title">
            Help &amp; Customer Support Hub
          </h1>
          <p className="hs-hero__subtitle">
            Have questions about your properties, rewards, local communities, or services? We're here to assist you at every step.
          </p>
        </div>
        <div className="hs-hero__actions">
          <a href={`tel:${PHONE}`} className="hs-contact hs-contact--primary">
            <FiPhone aria-hidden="true" />
            <span>Call +91 9818961783</span>
          </a>
          <a href={`mailto:${EMAIL}`} className="hs-contact hs-contact--secondary">
            <FiMail aria-hidden="true" />
            <span title={EMAIL}>{EMAIL}</span>
          </a>
        </div>
      </section>

      {/* Quick help (existing customer pages) */}
      <nav className="hs-quick" aria-label="Quick help">
        {QUICK_LINKS.map(({ label, description, to, icon: Icon }) => (
          <Link key={label} to={to} className="hs-quick__card">
            <span className="hs-quick__icon" aria-hidden="true">
              <Icon size={20} />
            </span>
            <span>
              <span className="hs-quick__label d-block">{label}</span>
              <span className="hs-quick__desc">{description}</span>
            </span>
          </Link>
        ))}
      </nav>

      <div className="hs-grid">
        {/* FAQs */}
        <section className="hs-card" aria-labelledby="hs-faq-title">
          <div className="hs-card__head">
            <span className="hs-card__icon" aria-hidden="true">
              <FiHelpCircle size={20} />
            </span>
            <div>
              <h2 className="hs-card__title" id="hs-faq-title">
                Frequently Asked Questions
              </h2>
              <p className="hs-card__subtitle">Quick answers to the most common questions regarding platform features.</p>
            </div>
          </div>

          <div className="hs-search">
            <FiSearch size={16} aria-hidden="true" />
            <input
              type="search"
              className="fk-input"
              placeholder="Search questions..."
              aria-label="Search questions"
              value={faqSearch}
              onChange={(e) => setFaqSearch(e.target.value)}
            />
          </div>

          {visibleFaqs.length === 0 ? (
            <p className="hs-faq__empty" role="status">
              No matching questions
            </p>
          ) : (
            <ul className="hs-faq">
              {visibleFaqs.map((faq) => {
                const isOpen = expandedFaq === faq.id;
                return (
                  <li key={faq.id} className={`hs-faq__item ${isOpen ? "hs-faq__item--open" : ""}`}>
                    <h3>
                      <button
                        type="button"
                        className="hs-faq__q"
                        aria-expanded={isOpen}
                        aria-controls={`faq-panel-${faq.id}`}
                        id={`faq-button-${faq.id}`}
                        onClick={() => handleToggleFaq(faq.id)}
                      >
                        <span>{faq.question}</span>
                        <FiChevronDown className="hs-faq__chev" size={18} aria-hidden="true" />
                      </button>
                    </h3>
                    <div
                      className="hs-faq__panel"
                      id={`faq-panel-${faq.id}`}
                      role="region"
                      aria-labelledby={`faq-button-${faq.id}`}
                    >
                      <div className="hs-faq__panel-inner">
                        <p className="hs-faq__a">{faq.answer}</p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Inquiry form (sticky on desktop) */}
        <aside className="hs-aside">
          <section className="hs-card" aria-labelledby="hs-form-title">
            <div className="hs-card__head">
              <span className="hs-card__icon" aria-hidden="true">
                <FiMessageSquare size={20} />
              </span>
              <div>
                <h2 className="hs-card__title" id="hs-form-title">
                  Send an Inquiry
                </h2>
                <p className="hs-card__subtitle">Our team will get back to you within 24 hours.</p>
              </div>
            </div>

            <form className="hs-form" onSubmit={handleSubmit} noValidate>
              <div className="hs-field">
                <div className="hs-label-row">
                  <label className="hs-label" htmlFor="hs-subject">
                    Subject / Topic<span className="hs-optional">Optional</span>
                  </label>
                </div>
                <input
                  id="hs-subject"
                  type="text"
                  className="fk-input"
                  placeholder="e.g. Question about Reward Points"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  autoComplete="off"
                />
              </div>

              <div className="hs-field">
                <div className="hs-label-row">
                  <label className="hs-label" htmlFor="hs-message">
                    Message Details<span className="hs-req" aria-hidden="true">*</span>
                  </label>
                  <span className={counterClass} aria-live="polite">
                    {message.length}/{MAX_CHARS}
                  </span>
                </div>
                <textarea
                  id="hs-message"
                  className="fk-input hs-textarea"
                  rows={6}
                  placeholder="Describe your issue or question in detail..."
                  value={message}
                  onChange={(e) => {
                    if (e.target.value.length <= MAX_CHARS) setMessage(e.target.value);
                  }}
                  aria-required="true"
                  aria-invalid={errorMsg ? "true" : undefined}
                  aria-describedby={errorMsg ? "hs-message-error" : undefined}
                />
                <div className="hs-textarea-foot">
                  {message && (
                    <button
                      type="button"
                      className="hs-clear"
                      onClick={() => {
                        setMessage("");
                        setErrorMsg("");
                      }}
                      disabled={submitting}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {errorMsg && (
                  <div className="hs-error" id="hs-message-error" role="alert">
                    {errorMsg}
                  </div>
                )}
              </div>

              <div aria-live="polite">
                {successMsg && (
                  <div className="hs-status hs-status--ok">
                    <FiCheckCircle size={16} aria-hidden="true" />
                    <span>{successMsg}</span>
                  </div>
                )}
              </div>

              <button type="submit" className="fk-btn fk-btn--primary hs-submit" disabled={submitting || !message.trim()}>
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    Sending Inquiry...
                  </>
                ) : (
                  <>
                    <FiSend size={15} aria-hidden="true" /> Send Message
                  </>
                )}
              </button>
            </form>

            <div className="hs-info">
              <div className="hs-info__row">
                <span className="hs-info__icon" aria-hidden="true">
                  <FiClock size={15} />
                </span>
                <span>Working Hours: Mon - Sat (9:00 AM - 7:00 PM IST)</span>
              </div>
              <div className="hs-info__row">
                <span className="hs-info__icon hs-info__icon--red" aria-hidden="true">
                  <FiMapPin size={15} />
                </span>
                <span>All Home Desire Headquarters, India</span>
              </div>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
